import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { SystemContext } from '@chakra-ui/react';
import { chakraUiTheme } from '../index';

export type DesignSystemThemes = 'chakraUiTheme' | 'wcn' | 'k12';

export function useTheme(key: DesignSystemThemes = 'chakraUiTheme') {
  const themeModules: Record<
    DesignSystemThemes,
    () => Promise<Record<string, SystemContext>> | Record<string, SystemContext>
  > = useMemo(
    () => ({
      chakraUiTheme: () => ({ chakraUiTheme }),
      wcn: () => import('../wcn'),
      k12: () => import('../k12'),
    }),
    []
  );
  /**
   * State
   */
  const [activeThemeDict, setActiveThemeDict] = useState<SystemContext>(chakraUiTheme);
  const [activeThemeKey, setActiveThemeKey] = useState<DesignSystemThemes>('chakraUiTheme');
  const [themeKeys] = useState<DesignSystemThemes[]>(Object.keys(themeModules) as DesignSystemThemes[]).sort();

  /**
   * Methods
   */
  const getThemeDict = useCallback(async (key: DesignSystemThemes) => {
    const themeModule = await themeModules[key]();
    return themeModule[key];
  }, []);

  const setTheme = useCallback(async (key: DesignSystemThemes) => {
    try {
      const dict = await getThemeDict(key);
      setActiveThemeDict(dict);
      setActiveThemeKey(key);
    } catch (e) {
      // Fallback to default
      setActiveThemeDict(chakraUiTheme);
      setActiveThemeKey('chakraUiTheme');
    }
  }, []);

  useEffect(() => {
    setTheme(key);
  }, [setTheme]);

  return {
    themeKeys,
    activeThemeKey,
    activeThemeDict,
    setActiveThemeDict: React.useCallback((key: DesignSystemThemes) => {
      setTheme(key);
    }, []),
  };
}
